import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mountain, Mail, Lock, Eye, EyeOff } from 'lucide-react';

type FormView = 'sign-in' | 'sign-up' | 'check-email';

interface AuthFormProps {
  onSuccess?: () => void;
}

export const AuthForm: React.FC<AuthFormProps> = ({ onSuccess }) => {
  const [view, setView] = useState<FormView>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const { signIn, sendEmailLink } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (password) {
      // Email + password sign-in
      try {
        await signIn(email, password);
        if (onSuccess) {
          onSuccess();
        }
      } catch (err: any) {
        if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
          setError('Invalid email or password');
        } else {
          setError(err.message || 'An error occurred');
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      // Send sign-in link
      try {
        await sendEmailLink(email);
        setSentEmail(email);
        setView('check-email');
      } catch (err: any) {
        setError(err.message || 'Failed to send sign-in link');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await sendEmailLink(email);
      setSentEmail(email);
      setView('check-email');
    } catch (err: any) {
      setError(err.message || 'Failed to send email link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setIsLoading(true);

    try {
      await sendEmailLink(sentEmail);
    } catch (err: any) {
      setError(err.message || 'Failed to resend email link');
    } finally {
      setIsLoading(false);
    }
  };

  const switchView = (newView: FormView) => {
    setError('');
    setView(newView);
  };

  const renderHeader = () => {
    let title = '';
    let subtitle = 'Access all Roam apps with one account';

    switch (view) {
      case 'sign-in':
        title = 'Sign in to Roam Apps';
        break;
      case 'sign-up':
        title = 'Create your Roam Apps account';
        break;
      case 'check-email':
        title = 'Check your email';
        subtitle = `We sent a sign-in link to ${sentEmail}`;
        break;
    }

    return (
      <div className="text-center">
        <div className="flex justify-center">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-3 rounded-xl">
            <Mountain className="h-10 w-10 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-3xl font-bold text-gray-900">{title}</h2>
        <p className="mt-2 text-sm text-gray-600">{subtitle}</p>
      </div>
    );
  };

  if (view === 'check-email') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full space-y-8">
          {renderHeader()}
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md text-sm">
              <p>Click the link in your email to continue. If you don't see it, check your spam folder.</p>
            </div>
            <button
              onClick={handleResend}
              disabled={isLoading}
              className="w-full py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending...' : 'Resend link'}
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => switchView('sign-in')}
                className="text-blue-600 hover:text-blue-500 text-sm"
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'sign-up') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full space-y-8">
          {renderHeader()}
          <form className="mt-8 space-y-6" onSubmit={handleSendLink}>
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none rounded-md relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Enter your email"
                  />
                  <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5 z-20 pointer-events-none" />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send sign-up link'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => switchView('sign-in')}
                  className="text-blue-600 hover:text-blue-500 text-sm"
                >
                  Already have an account? Sign in
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Default: sign-in view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-8">
        {renderHeader()}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none rounded-md relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your email"
                />
                <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5 z-20 pointer-events-none" />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded-md relative block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                />
                <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5 z-20 pointer-events-none" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 z-20"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Please wait...' : password ? 'Sign in' : 'Send sign-in link'}
            </button>

            <div className="text-center space-y-4">
              <button
                type="button"
                onClick={() => switchView('sign-up')}
                className="text-blue-600 hover:text-blue-500 text-sm"
              >
                Don't have an account? Sign up
              </button>
              <br />
              <button
                type="button"
                onClick={async () => {
                  if (!email.trim()) {
                    setError('Please enter your email address');
                    return;
                  }
                  setPassword('');
                  setError('');
                  setIsLoading(true);
                  try {
                    await sendEmailLink(email);
                    setSentEmail(email);
                    setView('check-email');
                  } catch (err: any) {
                    setError(err.message || 'Failed to send sign-in link');
                  } finally {
                    setIsLoading(false);
                  }
                }}
                className="text-gray-500 hover:text-blue-500 text-xs"
              >
                Forgot password? Send sign-in link
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
